import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppSettings } from '../app-settings';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { Router } from '@angular/router';
import jwt_decode from 'jwt-decode';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  // City
  private cityPage = new BehaviorSubject<number>(null);
  public cityStorage = this.cityPage.asObservable();

  public user: any = false;
  private profile!: any;

  private totemSessionRequest: Observable<any> | null = null;

  private profileObject = new BehaviorSubject<any>(null);
  public profileStorage = this.profileObject.asObservable();

  private userModeSubject = new BehaviorSubject<'user' | 'organizer'>(
    (localStorage.getItem('user_mode') as 'user' | 'organizer') || 'organizer'
  );
  public userMode$ = this.userModeSubject.asObservable();

  constructor(private http: HttpClient, private router: Router) {
    if (localStorage.getItem('jwt_token')) {
      this.getUsersMe().subscribe(res => { if (res) { this.user = res.data.id } })
    }
  }

  setUserMode(mode: 'user' | 'organizer') {
    localStorage.setItem('user_mode', mode);
    this.userModeSubject.next(mode);
  }

  getUserMode(): 'user' | 'organizer' {
    return this.userModeSubject.getValue();
  }

  getRoles() {
    let profile = this.getProfile();
    return profile?.roleFunctions.map((role: any) => role.role.code);
  }

  setProfile(user: any) {
    this.profile = user;
    this.profileObject.next(user);
  }

  isSuperAdmin() {
    let profile = this.getProfile();
    let rolesProfile = profile?.roles.map((role: any) => role.name);
    return rolesProfile?.includes('superadmin');
  }

  getProfile() {
    return this.profile;
  }

  /**
   * En el tótem no hay login de cliente: todas las compras cuelgan del usuario
   * del kiosco. Devuelve el perfil garantizando que hay sesión válida; si el
   * token falta, ha caducado o lo rechaza el back, se vuelve a autenticar con
   * las credenciales del tótem.
   * Fuera del tótem devuelve el perfil que haya (o null) sin tocar nada.
   */
  ensureTotemSession(): Observable<any> {
    if (!AppSettings.ENV_TOTEM) {
      return of(this.profile || null);
    }

    if (this.profile && this.hasValidToken()) {
      return of(this.profile);
    }

    if (!this.totemSessionRequest) {
      const source = this.hasValidToken() ? this.getUsersMe() : this.loginTotem();
      this.totemSessionRequest = source.pipe(
        catchError(() => this.loginTotem()),
        map(() => this.profile || null),
        // `user` lo leen componentes como card o favoritos; lo dejaba puesto el
        // antiguo check() del arranque del tótem.
        tap((profile: any) => { if (profile?.id != null) { this.user = profile.id; } }),
        catchError(() => of(null)),
        tap(() => { this.totemSessionRequest = null; }),
        shareReplay(1)
      );
    }

    return this.totemSessionRequest;
  }

  private loginTotem(): Observable<any> {
    localStorage.removeItem('jwt_token');
    return this.login(AppSettings.USER_TOTEM, AppSettings.PASS_TOTEM).pipe(
      switchMap(() => this.getUsersMe())
    );
  }

  private hasValidToken(): boolean {
    const token = localStorage.getItem('jwt_token');
    if (!token) {
      return false;
    }
    try {
      const decoded: any = jwt_decode(token);
      return !!decoded?.exp && decoded.exp > Math.floor(new Date().getTime() / 1000);
    } catch {
      return false;
    }
  }

  getMyData() {
    const url = `${AppSettings.API_URI_V2}/auth/me`;
    return this.http.get<any>(url).pipe(
      map(res => {
        if (res && res.data) {
          return res.data;
        }
        return null;
      }),
      catchError(err => {
        console.error('Error loading profile data:', err);
        return of(null);
      })
    );
  }

  checkCompanyAccess(id_company: number) {
    return this.getMyData().pipe(
      map(data => {
        if (!data || !data.roles) {
          return false;
        }

        const userRoles = data.roles.map((role: any) => role.name);

        // Superadmin siempre tiene acceso
        if (userRoles.includes('superadmin')) {
          return true;
        }

        // Debe ser admin
        if (!userRoles.includes('admin')) {
          return false;
        }

        // Verificar que tenga acceso a la company
        if (!data.companies) {
          return false;
        }

        return data.companies.some((company: any) => company.id === id_company);
      }),
      catchError(() => of(false))
    );
  }

  login(username: string, password: string) {
    const formHeaders = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    return this.http.post<any>(AppSettings.API_URI_V2 + '/auth/login', { email: username, password: password }, formHeaders).pipe(
      map(res => {
        if (res.access_token) {
          localStorage.setItem('jwt_token', res.access_token);
        }

        return res;
      })
    );
  }

  check(fromSeats: boolean = false, fromTotem: boolean = false) {
    return this.http.get<any>(AppSettings.API_URI_V2 + '/auth/me').pipe(
      map(res => {
        let data = res.data;
        let user = data.user || data; // Soporte para ambas estructuras
        if (user.active && user.id != null) {
          if (data.id_ciudad) {
            this.setCity(data.ciudad);
          }
          this.user = user.id;
          // Combinar datos de usuario con roles y companies
          let profileData = { ...user, roles: data.roles, companies: data.companies };
          this.setProfile(profileData);
          let roles = data.roles.map((role: any) => role.name);
          if(fromTotem) {
            return profileData;
          }
          if (roles.includes('admin') && !fromSeats) {
            roles.includes('superadmin') ? this.router.navigate(['/panel-control/list-business']) : this.router.navigate(['/panel-control/orders']);
          } else if (!fromSeats) {
            this.router.navigate(['/']);
          }
          return profileData;
        }
        return false;
      }, error => {
        return false;
      })
    );
  }

  register(jsonForm: any) {
    const formHeaders = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<any>(AppSettings.API_URI_V2 + '/auth/register', jsonForm, formHeaders).pipe(
      map(register => {
        return register;
      })
    );
  }

  logout() {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('refresh');
    let cookies = localStorage.getItem('cookies');
    localStorage.clear();
    localStorage.setItem('cookies', cookies);
    this.profileObject.next(null);
    this.profile = null;
    this.user = false;
    this.router.navigate(['/']).then(() => location.reload());
    const url = `${AppSettings.API_URI_V2}/logout`;

    return this.http.get<any>(url).pipe(
      map(
        data => {
          return data;
        }
      )
    );

  }

  setCity(city: number) {
    this.cityPage.next(city);
  }

  validateEmail(email: string, token: string) {
    const formHeaders = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    const url = `${AppSettings.API_URI_V2}/auth/verify-email`;
    return this.http.post<any>(url, { email: email, token: token }, formHeaders).pipe(
      map(
        data => {
          return data;
        }
      )
    );
  }

  resetPassword(payload: any) {
    return this.http.post<any>(AppSettings.API_URI_V2 + '/auth/reset-password-token', payload).pipe(
      map(data => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  loginGoogle(id: string, token: string) {
    const formHeaders = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    // Cambiar API
    return this.http.post<any>(AppSettings.API_URI_V2 + '/auth/google/login', { id: id, idToken: token }, formHeaders).pipe(
      map(res => {
        if (res.data) {
          localStorage.setItem('jwt_token', res.data.access_token);
          localStorage.setItem('refresh', res.data.access_token);
        }
        return res.data;
      })
    );
  }

  loginFacebook(payload: any) {
    const formHeaders = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    // Cambiar API
    return this.http.post<any>(AppSettings.API_URI + '/social_verify', { user: payload, fb: true }, formHeaders).pipe(
      map(jwt => {
        if (jwt.token) {
          localStorage.setItem('jwt_token', jwt.token);
          localStorage.setItem('refresh', jwt.refresh_token);
        }
        return jwt;
      })
    );
  }

  getUsersMe() {
    const url = `${AppSettings.API_URI_V2}/auth/me`;
    return this.http.get<any>(url).pipe(
      map((res) => {
        let data = res.data;
        let user = data.user || data;
        let profileData = { ...user, roles: data.roles, companies: data.companies };
        this.setProfile(profileData);
        return res;
      })
    );
  }

}
